// src/services/client.js

// Base API URL - should match other services
const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY


/**
 * Fetch the current user's client profile
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object|null>} - Client profile object or null if not found
 */
export async function fetchMyClientProfile(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/clients/protected/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    if (response.status === 404) {
      return null; // Client profile not found
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching client profile:', error);
    throw error;
  }
}

/**
 * Create a new client profile for the current user
 * @param {string} token - JWT authentication token
 * @param {Object} clientData - Client profile data
 * @returns {Promise<Object>} - Created client profile
 */
export async function createClientProfile(token, clientData) {
  const response = await fetch(`${API_BASE_URL}/clients/protected`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify(clientData)
  });
  
  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create client profile');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Update the current user's client profile
 * @param {string} token - JWT authentication token
 * @param {Object} clientData - Updated client profile data
 * @returns {Promise<Object>} - Updated client profile
 */
export async function updateClientProfile(token, clientData) {
  const response = await fetch(`${API_BASE_URL}/clients/protected/me`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify(clientData)
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Client profile not found');
    } else if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid data');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Check if the current user has a client profile
 * @param {string} token - JWT authentication token
 * @returns {Promise<boolean>} - True if user has a client profile
 */
export async function hasClientProfile(token) {
  try {
    const profile = await fetchMyClientProfile(token);
    return profile !== null;
  } catch (error) {
    console.error('Error checking client profile:', error);
    return false;
  }
}

/**
 * For admin use only - fetch a specific client profile by ID
 * @param {string} token - JWT authentication token
 * @param {string} clientId - ID of the client to fetch
 * @returns {Promise<Object>} - Client profile
 */
export async function fetchClientById(token, clientId) {
  const response = await fetch(`${API_BASE_URL}/clients/admin?id=${clientId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Client not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to access this client');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * For admin use only - update a specific client profile
 * @param {string} token - JWT authentication token
 * @param {string} clientId - ID of the client to update
 * @param {Object} clientData - Updated client data
 * @returns {Promise<Object>} - Updated client profile
 */
export async function updateClientById(token, clientId, clientData) {
  const response = await fetch(`${API_BASE_URL}/clients/admin/${clientId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify(clientData)
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Client not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to update this client');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * For admin use only - delete a client profile
 * @param {string} token - JWT authentication token
 * @param {string} clientId - ID of the client to delete
 * @returns {Promise<void>}
 */
export async function deleteClient(token, clientId) {
  const response = await fetch(`${API_BASE_URL}/clients/admin/${clientId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Client not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to delete this client');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  // DELETE typically returns no content
  return;
}

/**
 * Upload a client profile image
 * @param {string} token - JWT authentication token
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - Response with image URL info or error details
 */
export async function uploadClientProfileImage(token, imageFile) {
  // Create a FormData object to send the file
  const formData = new FormData();
  formData.append('file', imageFile);
  
  try {
    // Use the correct API path
    const apiUrl = `${API_BASE_URL}/clients/protected/profile-image`;    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: formData
    });
        
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      // Return an object with error info instead of throwing
      return {
        success: false,
        error: errorMessage,
        serverIssue: true
      };
    }
    
    // Handle successful response
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return {
          success: true,
          image_url: data.presigned_url || data.profile_image
        };
      } else {
        const textResponse = await response.text();
        return { 
          success: true,
          message: 'Image uploaded successfully', 
          text_response: textResponse 
        };
      }
    } catch (parseError) {
      console.error('Error parsing success response:', parseError);
      return {
        success: false,
        error: 'Could not parse server response',
        serverIssue: true
      };
    }
  } catch (error) {
    console.error('Error uploading profile image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get the client's profile image URL
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - URL of the profile image or error details
 */
export async function getClientProfileImage(token) {
  try {
    const apiUrl = `${API_BASE_URL}/clients/protected/me/profile-image`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
        
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      success: true,
      image_url: data.presigned_url || null
    };
  } catch (error) {
    console.error('Error in getClientProfileImage:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Recover client password using security answers
 * @param {string} token - JWT authentication token
 * @param {Object} securityAnswers - Security answers object
 * @returns {Promise<Object>} - Recovery response with password
 */
export async function recoverClientPassword(token) {
  const response = await fetch(`${API_BASE_URL}/clients/protected/me/recover-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}